// ============================================================
// NotificationChannelsCard.js — per-Client channel overrides for
// the 34 NotificationTypes. Toggles Teams/Email; both off displays
// a warning. Per-notificationTypeId PATCH; Save loops over dirty.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import Switch from '../../components/Switch';
import { toast } from '../../components/Toaster';
import {
  getClientNotificationChannelConfig, patchClientNotificationChannelConfigOne,
} from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

// Normalises whichever shape the API hands back into the
// { teams, email } pair the UI tracks. Today's backend returns
// arrays (defaultChannels, effectiveChannels are NVARCHAR(MAX) csv
// at rest but emitted as JS arrays). A legacy csv-string shape is
// still tolerated so the card doesn't blow up if the backend ever
// flips back, and null/undefined yields an empty set.
function parseChannels(channels) {
  let tokens;
  if (Array.isArray(channels))           tokens = channels;
  else if (typeof channels === 'string') tokens = channels.split(',');
  else                                   tokens = [];
  const set = new Set(tokens.map((s) => String(s).trim()).filter(Boolean));
  return { teams: set.has('teams'), email: set.has('email') };
}

function formatChannelsDisplay(channels) {
  if (Array.isArray(channels))           return channels.join(', ') || '—';
  if (typeof channels === 'string')      return channels || '—';
  return '—';
}

export default function NotificationChannelsCard({ clientId }) {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getClientNotificationChannelConfig(clientId);
      const list = res.items || [];
      setItems(list);
      setDraft(Object.fromEntries(list.map((r) => {
        // effectiveChannels is csv of resolved channels for this client.
        const eff = parseChannels(r.effectiveChannels || r.defaultChannels);
        return [r.notificationTypeId, { teams: eff.teams, email: eff.email }];
      })));
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  function setRowChannel(id, channel, value) {
    setDraft((prev) => ({ ...prev, [id]: { ...prev[id], [channel]: value } }));
  }

  async function save() {
    setSaving(true);
    const dirty = items.filter((r) => {
      const d = draft[r.notificationTypeId];
      const eff = parseChannels(r.effectiveChannels || r.defaultChannels);
      return d.teams !== eff.teams || d.email !== eff.email;
    });
    let succeeded = 0, failed = 0;
    for (const r of dirty) {
      const d = draft[r.notificationTypeId];
      const channelsOverride = [];
      if (d.teams) channelsOverride.push('teams');
      if (d.email) channelsOverride.push('email');
      try {
        await patchClientNotificationChannelConfigOne(clientId, r.notificationTypeId, {
          channelsOverride,
          isEnabled: channelsOverride.length > 0,
        });
        succeeded++;
      } catch (err) {
        failed++;
        toast.error(`${r.code}: ${translateError(err)}`);
      }
    }
    if (succeeded > 0) toast.success(`Saved ${succeeded} change${succeeded === 1 ? '' : 's'}`);
    if (failed === 0) await fetchData();
    setSaving(false);
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error)   return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  return (
    <div>
      <table className="pc-table">
        <thead>
          <tr>
            <th>Notification Type</th>
            <th style={{ width: 180 }}>Platform Default</th>
            <th style={{ width: 80 }}>Teams</th>
            <th style={{ width: 80 }}>Email</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const d = draft[r.notificationTypeId] || { teams: false, email: false };
            const bothOff = !d.teams && !d.email;
            return (
              <tr key={r.notificationTypeId}>
                <td>
                  <div style={{ fontSize: 13 }}>{r.code}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{r.description}</div>
                  {bothOff && <span className="pc-warn">⚠ Both channels off — type silenced</span>}
                </td>
                <td style={{ fontSize: 12 }}>{formatChannelsDisplay(r.defaultChannels)}</td>
                <td>
                  <Switch
                    checked={d.teams}
                    onChange={(next) => setRowChannel(r.notificationTypeId, 'teams', next)}
                    ariaLabel={`${r.code} Teams`}
                  />
                </td>
                <td>
                  <Switch
                    checked={d.email}
                    onChange={(next) => setRowChannel(r.notificationTypeId, 'email', next)}
                    ariaLabel={`${r.code} Email`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="pc-actions">
        <button type="button" className="pc-btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
